<template>
  <div class="bg-white border rounded-lg shadow-sm hover:shadow transition-shadow duration-200">
    <div class="p-2 sm:p-4">
      <div class="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <div class="w-full sm:flex-1">
          <input 
            class="w-full p-2 border rounded focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none"
            v-model="localItem.description" 
            placeholder="Item Description" 
            @input="update" 
          />
        </div>
        <div class="flex flex-wrap items-center justify-end gap-2">
          <!-- Badges -->
          <div class="flex gap-1">
            <span class="bg-blue-50 text-blue-700 text-sm px-2 py-0.5 rounded whitespace-nowrap">
              {{ localItem.options?.length || 0 }}
            </span>
            <span v-if="!isUsingDefaultOptions && parentChecklist?.defaultOptions" 
                  class="bg-orange-50 text-orange-700 text-sm px-2 py-0.5 rounded flex items-center gap-1 whitespace-nowrap"
                  title="Custom options">
              <span class="material-icons text-sm">tune</span>
            </span>
          </div>

          <!-- Actions -->
          <div class="flex gap-1">
            <button 
              @click="showOptions = !showOptions"
              class="text-gray-400 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-100"
            >
              <span class="material-icons text-xl" :class="{ 'rotate-180': showOptions }">
                expand_more
              </span>
            </button>
            <button 
              @click="deleteItem"
              class="text-red-400 hover:text-red-600 p-1.5 rounded-full hover:bg-red-50"
            >
              <span class="material-icons text-xl">delete</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Options Section -->
      <div v-show="showOptions" 
           class="mt-3 pt-3 border-t space-y-2"
      >
        <div class="flex items-center justify-between mb-2">
          <h4 class="text-sm font-medium text-gray-600">Options</h4>
          <button 
            @click="addNewOption"
            class="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
          >
            <span class="material-icons text-sm">add</span>
            Add Option
          </button>
        </div>
        
        <div class="space-y-2 max-w-full overflow-x-hidden">
          <div v-for="option in localItem.options" :key="option.id">
            <OptionEditor :option="option" @update="updateOption" @delete="deleteOption" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, computed } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import OptionEditor from './OptionEditor.vue';

export default {
  components: { 
    OptionEditor 
  },
  props: {
    item: Object,
    parentChecklist: Object,
  },
  emits: ['update', 'delete'],
  
  setup(props, { emit }) {
    const localItem = ref({ ...props.item });
    const showOptions = ref(false);
    const lastDefaultOptions = ref(null);

    // Track if using default options
    const isUsingDefaultOptions = computed(() => {
      const defaultOpts = props.parentChecklist?.defaultOptions;
      if (!defaultOpts || !localItem.value.options) return false;
      
      // Normalize and sort options for reliable comparison
      const normalizeAndSortOptions = (options) => {
        return options
          .map(opt => ({
            text: opt.text.toLowerCase().trim(),
            type: opt.type,
            unit: (opt.unit || '').toLowerCase().trim()
          }))
          .sort((a, b) => a.text.localeCompare(b.text));
      };

      const defaultNormalized = normalizeAndSortOptions(defaultOpts);
      const currentNormalized = normalizeAndSortOptions(localItem.value.options);

      // Compare stringified versions for exact match
      return JSON.stringify(defaultNormalized) === JSON.stringify(currentNormalized);
    });

    watch(
      () => props.item,
      (newVal) => {
        localItem.value = { ...newVal };
      }
    );

    const update = () => {
      emit('update', localItem.value);
    };

    const deleteItem = () => {
      emit('delete', localItem.value.id);
    };

    const addNewOption = () => {
      if (!localItem.value.options) {
        localItem.value.options = [];
      }
      
      localItem.value.options.push({
        id: uuidv4(),
        text: 'New Option',
        type: 'radio',
        unit: '',
      });
      update();
    };

    const updateOption = (updatedOption) => {
      const index = localItem.value.options.findIndex((opt) => opt.id === updatedOption.id);
      if (index !== -1) {
        localItem.value.options[index] = updatedOption;
        update();
      }
    };

    const deleteOption = (id) => {
      localItem.value.options = localItem.value.options.filter((opt) => opt.id !== id);
      update();
    };

    const applyDefaultOptions = (options) => {
      lastDefaultOptions.value = options;
      localItem.value.options = options;
      update();
    };

    return {
      localItem,
      showOptions,
      isUsingDefaultOptions,
      update,
      deleteItem,
      addNewOption,
      updateOption,
      deleteOption,
      applyDefaultOptions,
    };
  },
};
</script>

<style scoped>
@media (max-width: 640px) {
  .flex-col {
    width: 100%;
  }
  
  input {
    width: 100%;
    font-size: 0.95rem;
    box-sizing: border-box;
  }
  
  /* Adjust container padding */
  .p-2 {
    padding: 0.75rem;
  }
  
  /* Ensure proper input container width */
  .w-full {
    width: 100% !important;
    min-width: 0;
  }
  
  .material-icons {
    font-size: 1.25rem;
  }
  
  .p-1\.5 {
    padding: 0.35rem;
  }
  
  .space-y-2 > * + * {
    margin-top: 0.375rem;
  }
  
  .border-t {
    margin-top: 0.5rem;
  }
  
  .rounded-lg {
    border-radius: 0.5rem;
  }
}
</style>